import { useEffect, useRef, useState } from 'react';
import { ApiFetch } from '../utils/fetch';
import { useToast } from './toast/useToast';

export type ExportFormat = 'csv' | 'excel';

const FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV (.csv)',
  excel: 'Excel (.xlsx)',
};

const EXTENSIONS: Record<ExportFormat, string> = {
  csv: 'csv',
  excel: 'xlsx',
};

interface ExportButtonProps {
  /**
   * BFF export path WITHOUT the `format` query — e.g. `/merchants/export`.
   * This component appends `format` with the correct separator.
   *
   * ⚠️ Every current caller passes a BARE path, so exports return the FULL
   * dataset and deliberately ignore whatever the page is filtered to — hence the
   * default "Export all" label. Extra query params would be forwarded (the routes
   * take an untyped `@Query() Record<string,string>`), but only `format` is a
   * DECLARED parameter on any of them, so whether the backend honours a filter is
   * unverified — don't append one without confirming it downstream first, or the
   * label will start lying in the other direction.
   */
  endpoint: string;
  /** Filename stem; the extension is added per format. e.g. "merchants". */
  filenameBase: string;
  /** Formats to offer. Every PE-2126 route supports both, so this is rarely set. */
  formats?: ExportFormat[];
  label?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Shared "Export all" control for the platform-wide data exports (PE-2127, backend
 * PE-2126). Every export route follows the same contract — `GET <resource>/export`
 * with `?format=csv|excel`, streaming a file — so this is one component reused
 * across all the admin/merchant data views rather than a button per page.
 *
 * The download goes through `ApiFetch.download`, which carries the bearer, the
 * x-tenant-id and the shared 401 handling; the server's Content-Disposition wins
 * over `filenameBase` when present.
 *
 * NB: the tenant-scoped exports (products/orders/bookings/custom-orders) require
 * an x-tenant-id, so pages behind a merchant selector should pass `disabled`
 * while no merchant is chosen rather than firing a request that 400s.
 */
export function ExportButton({
  endpoint,
  filenameBase,
  formats = ['csv', 'excel'],
  label = 'Export all',
  disabled = false,
  className = '',
}: ExportButtonProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape — matches the other dropdowns in the admin.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const runExport = async (format: ExportFormat) => {
    setBusy(format);
    setOpen(false);
    try {
      const separator = endpoint.includes('?') ? '&' : '?';
      const stamp = new Date().toISOString().slice(0, 10);
      await ApiFetch.download(
        `${endpoint}${separator}format=${format}`,
        `${filenameBase}-${stamp}.${EXTENSIONS[format]}`,
      );
      toast.success('Export downloaded');
    } catch (err) {
      // downloadRequest only toasts when the SERVER returned an error status, and
      // marks those `toasted`. Anything else — offline, DNS/CORS, a rejection in
      // res.blob() — reaches here unreported, and without this the button would
      // just flip back to its label with no explanation of why nothing saved.
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error('Export failed. Please check your connection and try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  // Single-format callers get a plain button — no menu for one option.
  if (formats.length === 1) {
    return (
      <button
        type="button"
        disabled={disabled || busy !== null}
        onClick={() => runExport(formats[0])}
        className={`h-[40px] px-4 rounded-[8px] border border-border bg-background text-sm text-foreground hover:bg-muted disabled:opacity-50 transition-colors ${className}`}
      >
        {busy ? 'Exporting…' : label}
      </button>
    );
  }

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        disabled={disabled || busy !== null}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="h-[40px] px-4 rounded-[8px] border border-border bg-background text-sm text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
      >
        {busy ? 'Exporting…' : label}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-[180px] rounded-[10px] border border-border bg-card shadow-lg overflow-hidden"
        >
          {formats.map((f) => (
            <button
              key={f}
              role="menuitem"
              type="button"
              onClick={() => runExport(f)}
              className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              {FORMAT_LABELS[f]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
